import React, { useState } from "react";
import { Button, Input, Select, SelectItem } from "@nextui-org/react";
import { FaXTwitter, FaInstagram, FaDiscord, FaYoutube, FaTwitch, FaGithub, FaTiktok, FaLinkedin, FaTelegram, FaMastodon } from "react-icons/fa6";
import { FaTrash, FaPlus } from "react-icons/fa6";

export const SOCIAL_PLATFORMS = [
  { key: "x", label: "X (Twitter)", Icon: FaXTwitter },
  { key: "instagram", label: "Instagram", Icon: FaInstagram },
  { key: "discord", label: "Discord", Icon: FaDiscord },
  { key: "youtube", label: "YouTube", Icon: FaYoutube },
  { key: "twitch", label: "Twitch", Icon: FaTwitch },
  { key: "github", label: "GitHub", Icon: FaGithub },
  { key: "tiktok", label: "TikTok", Icon: FaTiktok },
  { key: "linkedin", label: "LinkedIn", Icon: FaLinkedin },
  { key: "telegram", label: "Telegram", Icon: FaTelegram },
  { key: "mastodon", label: "Mastodon", Icon: FaMastodon },
];

const PLATFORM_MAP = Object.fromEntries(SOCIAL_PLATFORMS.map((p) => [p.key, p]));

// Read-only row of social icons, shown on the public profile.
export function SocialLinksDisplay({ links = [], className = "" }) {
  const valid = links.filter((l) => PLATFORM_MAP[l.platform]);
  if (valid.length === 0) return null;
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {valid.map((l) => {
        const { Icon, label } = PLATFORM_MAP[l.platform];
        return (
          <a
            key={l.platform}
            href={l.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={label}
            className="text-foreground-500 hover:text-primary transition-colors"
          >
            <Icon size={18} />
          </a>
        );
      })}
    </div>
  );
}

// Editable list used in profile settings: add up to 6 { platform, url } pairs.
export function SocialLinksEditor({ links = [], onChange }) {
  const [platform, setPlatform] = useState("x");
  const [url, setUrl] = useState("");

  const usedPlatforms = new Set(links.map((l) => l.platform));
  const available = SOCIAL_PLATFORMS.filter((p) => !usedPlatforms.has(p.key));

  const add = () => {
    if (!url.trim() || !available.find((p) => p.key === platform)) return;
    onChange([...links, { platform, url: url.trim() }]);
    setUrl("");
    const next = available.find((p) => p.key !== platform);
    if (next) setPlatform(next.key);
  };

  const remove = (key) => {
    onChange(links.filter((l) => l.platform !== key));
  };

  return (
    <div className="flex flex-col gap-3">
      {links.length > 0 && (
        <div className="flex flex-col gap-2">
          {links.map((l) => {
            const meta = PLATFORM_MAP[l.platform];
            if (!meta) return null;
            const { Icon, label } = meta;
            return (
              <div key={l.platform} className="flex items-center gap-2 border border-default-100 rounded-medium px-3 py-2">
                <Icon size={16} className="text-foreground-500 shrink-0" />
                <span className="text-small font-medium w-24 shrink-0">{label}</span>
                <span className="text-small text-foreground-500 truncate flex-1">{l.url}</span>
                <Button isIconOnly size="sm" variant="light" color="danger" onPress={() => remove(l.platform)} aria-label={`Remove ${label}`}>
                  <FaTrash size={13} />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {links.length < 6 && available.length > 0 && (
        <div className="flex items-end gap-2 flex-wrap">
          <Select
            label="Platform"
            selectedKeys={[platform]}
            onSelectionChange={(keys) => setPlatform(Array.from(keys)[0])}
            size="sm"
            className="max-w-[160px]"
          >
            {available.map((p) => (
              <SelectItem key={p.key}>{p.label}</SelectItem>
            ))}
          </Select>
          <Input
            label="Profile URL"
            value={url}
            onValueChange={setUrl}
            size="sm"
            variant="bordered"
            placeholder="https://..."
            className="flex-1 min-w-[160px]"
          />
          <Button isIconOnly size="sm" variant="flat" color="primary" onPress={add} aria-label="Add social link">
            <FaPlus size={13} />
          </Button>
        </div>
      )}
      {links.length >= 6 && <p className="text-tiny text-foreground-400">Maximum of 6 social links.</p>}
    </div>
  );
}
